/**
 * Validates draft metadata against required fields defined in metadata.yaml
 * 
 * @param {Object} metadata - The metadata object to validate
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validateDatasetMetadata = (metadata) => {
  const errors = [];

  if (!metadata) {
    return { isValid: false, errors: ["Metadata is missing"] };
  }

  // Validate assetType (required)
  if (!metadata.assetType) {
    errors.push("Asset type is required");
  }

  // Validate datasetType (required)
  if (!metadata.datasetType) {
    errors.push("Dataset type is required");
  } else {
    if (!metadata.datasetType.datasetTypeCode) {
      errors.push("Dataset type code is required");
    }
  }

  // Validate publicationDate (required)
  if (!metadata.publicationDate) {
    errors.push("Publication date is required");
  }

  // Validate titles (required array)
  if (!metadata.titles || metadata.titles.length === 0) {
    errors.push("At least one title is required");
  } else {
    metadata.titles.forEach((title, index) => {
      if (!title.titleText) {
        errors.push(`Title ${index + 1}: Title text is required`);
      }
    });
  }

  // Validate descriptions (required array)
  if (!metadata.descriptions || metadata.descriptions.length === 0) {
    errors.push("At least one description is required");
  } else {
    metadata.descriptions.forEach((desc, index) => {
      if (!desc.descriptionText) {
        errors.push(`Description ${index + 1}: Description text is required`);
      }
      if (!desc.descriptionType) {
        errors.push(`Description ${index + 1}: Description type is required`);
      }
    });
  }

  // Validate keywords (required array)
  if (!metadata.keywords || metadata.keywords.length === 0) {
    errors.push("At least one keyword is required");
  } else {
    metadata.keywords.forEach((keyword, index) => {
      if (!keyword.keywordLabel) {
        errors.push(`Keyword ${index + 1}: Keyword label is required`);
      }
    });
  }

  // Validate siteReferences (required array)
  if (!metadata.siteReferences || metadata.siteReferences.length === 0) {
    errors.push("At least one site reference is required");
  } else {
    metadata.siteReferences.forEach((site, index) => {
      if (!site.siteName) {
        errors.push(`Site reference ${index + 1}: Site name is required`);
      }
      if (!site.siteID) {
        errors.push(`Site reference ${index + 1}: Site ID is required`);
      }
    });
  }

  // Validate licenses (required array)
  if (!metadata.licenses || metadata.licenses.length === 0) {
    errors.push("At least one license is required");
  } else {
    metadata.licenses.forEach((license, index) => {
      if (!license.licenseCode) {
        errors.push(`License ${index + 1}: License code is required`);
      }
    });
  }

  // Validate responsibleOrganizations (required array)
  if (!metadata.responsibleOrganizations || metadata.responsibleOrganizations.length === 0) {
    errors.push("At least one responsible organization is required");
  } else {
    metadata.responsibleOrganizations.forEach((org, index) => {
      if (!org.organizationName) {
        errors.push(`Responsible organization ${index + 1}: Organization name is required`);
      }
    });
  }

  // Validate creators (required array)
  if (!metadata.creators || metadata.creators.length === 0) {
    errors.push("At least one creator is required");
  } else {
    metadata.creators.forEach((creator, index) => {
      if (!creator.creatorFamilyName) {
        errors.push(`Creator ${index + 1}: Family name is required`);
      }
      if (!creator.creatorAffiliation) {
        errors.push(`Creator ${index + 1}: Affiliation is required`);
      }
    });
  }

  // Validate language (required)
  if (!metadata.language) {
    errors.push("Language is required");
  }

  // Validate dataLevel (required)
  if (!metadata.dataLevel) {
    errors.push("Data level is required");
  } else {
    if (!metadata.dataLevel.dataLevelCode) {
      errors.push("Data level code is required");
    }
  }

  // Validate geoLocations (required array)
  if (!metadata.geoLocations || metadata.geoLocations.length === 0) {
    errors.push("At least one geo location is required");
  }

  // Validate temporalCoverages (required array)
  if (!metadata.temporalCoverages || metadata.temporalCoverages.length === 0) {
    errors.push("At least one temporal coverage is required");
  } else {
    metadata.temporalCoverages.forEach((coverage, index) => {
      if (!coverage.startDate) {
        errors.push(`Temporal coverage ${index + 1}: Start date is required`);
      }
    });
  }

  // Validate habitatReferences (required array)
  if (!metadata.habitatReferences || metadata.habitatReferences.length === 0) {
    errors.push("At least one habitat reference is required");
  } else {
    metadata.habitatReferences.forEach((habitat, index) => {
      if (!habitat.soHabitatCode) {
        errors.push(`Habitat reference ${index + 1}: SO habitat code is required`);
      }
    });
  }

  // Conditional validation: If methods exist, validate their structure
  if (metadata.methods && metadata.methods.length > 0) {
    metadata.methods.forEach((method, index) => {
      // sampling is optional, but if present, its structure should be valid
      if (method.sampling) {
        // sampling properties are optional
      }
      // steps is an optional array
      if (method.steps && method.steps.length > 0) {
        method.steps.forEach((step, stepIndex) => {
          // step properties are optional
        });
      }
    });
  }

  // Conditional validation: If temporalResolution exists, validate it
  if (metadata.temporalResolution) {
    if (metadata.temporalResolution.temporalResolutionValue === undefined || 
        metadata.temporalResolution.temporalResolutionValue === null) {
      errors.push("Temporal resolution value is required when temporal resolution is provided");
    }
    if (!metadata.temporalResolution.temporalResolutionUnit) {
      errors.push("Temporal resolution unit is required when temporal resolution is provided");
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};
